import { createApi } from '@reduxjs/toolkit/query/react';

import { AiAdmin } from '../endpoints';
import {
  getBaseQuery,
  TransformedErrorResponseWithNotification,
} from '../generic';
import { HTTP_METHOD } from '../constants';

/** Matches backend TranslateItemRequestDto */
export interface TranslateItemRequest {
  language: string;
  data?: {
    name: string;
    description: string;
  };
}

/** Matches backend TranslateItemResponseDto / TranslateItemDataDto */
export interface TranslateItemResponse {
  language: string;
  data: {
    name: string;
    description: string;
  };
}

/** Matches backend ChatMessageDto: role 'user' | 'model', content */
export interface ChatMessageDto {
  role: 'user' | 'model';
  content: string;
}

/** Matches backend AiAssistantChatRequestDto */
export interface ChatRequest {
  messages: ChatMessageDto[];
}

export const aiAdminApi = createApi({
  reducerPath: 'aiAdminApi',
  baseQuery: getBaseQuery(),
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    chat: builder.mutation<string, ChatRequest>({
      query(body) {
        return {
          url: AiAdmin.CHAT,
          method: HTTP_METHOD.POST,
          body,
        };
      },
      transformResponse: (response: { result: string }) => response.result,
      ...TransformedErrorResponseWithNotification(),
    }),
    generateExerciseDescription: builder.mutation<string, { name: string }>({
      query({ name }) {
        return {
          url: AiAdmin.GENERATE_EXERCISE_DESCRIPTION,
          method: HTTP_METHOD.POST,
          body: { name },
        };
      },
      transformResponse: (response: { result: string }) => response.result,
      ...TransformedErrorResponseWithNotification(),
    }),
    translateMissing: builder.mutation<
      TranslateItemResponse[],
      { items: TranslateItemRequest[] }
    >({
      query({ items }) {
        return {
          url: AiAdmin.TRANSLATE_MISSING,
          method: HTTP_METHOD.POST,
          body: { items },
        };
      },
      transformResponse: (response: {
        result?: TranslateItemResponse[] | string;
        data?: { result?: TranslateItemResponse[] | string };
      }): TranslateItemResponse[] => {
        const rawResult =
          response?.result ?? response?.data?.result ?? response;
        if (Array.isArray(rawResult)) return rawResult;
        if (typeof rawResult === 'string') {
          try {
            const parsed = JSON.parse(rawResult);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useChatMutation,
  useGenerateExerciseDescriptionMutation,
  useTranslateMissingMutation,
} = aiAdminApi;
